import { useState } from 'react';
import { toast } from 'react-hot-toast';

import http from 'src/api/endpoints';
import { Button } from 'src/components';
import AuthContainer from 'src/Containers/AuthContainer';
import OnboardingHeaderWrapper from 'src/components/Wrapper/OnboardingHeaderWrapper';
import ConfirmationCodeInput from '../component/ConfirmationCodeInput';
import useLoading from 'src/hooks/useLoading';
import { PARENT_URL } from 'src/config/parentURL';
import Spinner from 'src/assets/images/inline-spinner.svg';

type EmailVerifiedData = {
  isConfirmedEmail: boolean;
  nextStep: any;
  email: string;
  userExists: boolean;
};

type EmailVerifiedProps = EmailVerifiedData & {
  updateFields: (fields: Partial<EmailVerifiedData>) => void;
  nextStep: () => void;
};

const EmailVerified = ({
  nextStep,
  updateFields,
  email,
}: EmailVerifiedProps) => {
  const [code, setCode] = useState<string>('');
  const [isReset, setIsReset] = useState<boolean>(false);
  const [key] = useState<number>(0);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const {
    isLoading: isResendCodeLoading,
    startLoading: resendStartLoading,
    stopLoading: resendStopLoading,
  } = useLoading();

  const onHandleVerifyDomain = async () => {
    startLoading();
    if (email && code) {
      try {
        const response = await http.verifyDomain(
          {
            email,
            code,
          },
          PARENT_URL.BASE_SERVER
        );
        const { message, userExists } = response || {};
        if (message && message === 'Email verified successfully.') {
          updateFields({
            isConfirmedEmail: true,
            userExists: userExists ? true : false,
          });
          nextStep();
        } else {
          toast.error('Email confirmation failed');
        }
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        } else {
          toast.error('Something went wrong, please try again later!!');
        }
      } finally {
        stopLoading();
      }
    }
  };

  const onHandleResendCode = async () => {
    if (!email) {
      toast.error(
        'No email find to send the verification code, please reset signup flow'
      );
    }
    setCode('');
    setIsReset(true);
    resendStartLoading();
    try {
      const response = await http.getVerificationCode(
        {
          email,
        },
        PARENT_URL.BASE_SERVER
      );
      const { message } = response || {};
      if (message && message === 'Email Verification code sent successfully') {
        toast.success('Email Verification code sent successfully');
      } else {
        toast.error('Email verification failed');
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error('Something went wrong, please try again later!!');
      }
    } finally {
      resendStopLoading();
      setIsReset(false);
    }
  };

  return (
    <>
      <AuthContainer>
        <OnboardingHeaderWrapper
          title="Please confirm your email address"
          index={1}
          link="LOGIN"
        />
        <div key={key}>
          <ConfirmationCodeInput
            isReset={isReset}
            onChange={(value) => {
              setCode(value);
            }}
          />
        </div>
        <p
          className="text-lightBlue text-sm font-bold font-SFProDisplay leading-[18px] mt-3 cursor-pointer flex gap-1 align-middle"
          onClick={onHandleResendCode}
        >
          {isResendCodeLoading && (
            <img
              src={Spinner}
              alt="spinner"
              className="h-6 mt-[-3.8px] animate-spin "
            />
          )}
          <button
            disabled={isResendCodeLoading}
            className={
              isResendCodeLoading ? 'cursor-not-allowed' : 'cursor-pointer'
            }
          >
            Resend Code
          </button>
        </p>
        <Button
          variant="normal"
          size="sm"
          label="Continue"
          className="block w-full mx-0 mt-8 md:mt-20"
          disabled={code.length < 6}
          onClick={() => onHandleVerifyDomain()}
          isLoading={isLoading}
        />
      </AuthContainer>
    </>
  );
};
export default EmailVerified;
